
const DECIMAL = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/;

export function isDecimal(value: string | null | undefined): boolean {
  return typeof value === 'string' && DECIMAL.test(value.trim());
}

interface Scaled {
  units: bigint;
  scale: number;
}

function scale(value: string): Scaled {
  const text = value.trim();
  if (!DECIMAL.test(text)) {
    throw new RangeError(`Not a decimal string: ${JSON.stringify(value)}`);
  }

  const negative = text.startsWith('-');
  const body = negative ? text.slice(1) : text;
  const [int = '', frac = ''] = body.split('.');
  const units = BigInt(`${int || '0'}${frac}`);

  return { units: negative ? -units : units, scale: frac.length };
}

function unscale({ units, scale: dp }: Scaled): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(dp + 1, '0');
  const int = digits.slice(0, digits.length - dp);
  const frac = dp > 0 ? `.${digits.slice(digits.length - dp)}` : '';
  const body = `${int}${frac}`;

  return negative && units !== 0n ? `-${body}` : body;
}

function common(a: Scaled, b: Scaled): [bigint, bigint, number] {
  const dp = Math.max(a.scale, b.scale);
  return [a.units * 10n ** BigInt(dp - a.scale), b.units * 10n ** BigInt(dp - b.scale), dp];
}

export function addDecimal(a: string, b: string): string {
  const [x, y, dp] = common(scale(a), scale(b));
  return unscale({ units: x + y, scale: dp });
}

export function subDecimal(a: string, b: string): string {
  const [x, y, dp] = common(scale(a), scale(b));
  return unscale({ units: x - y, scale: dp });
}

export function sumDecimal(values: Iterable<string>): string {
  let total = '0';
  for (const value of values) total = addDecimal(total, value);
  return total;
}

export function mulDecimal(a: string, b: string): string {
  const x = scale(a);
  const y = scale(b);
  return unscale({ units: x.units * y.units, scale: x.scale + y.scale });
}

export function divDecimal(a: string, b: string, dp = 4): string | null {
  const [x, y] = common(scale(a), scale(b));
  if (y === 0n) return null;

  const negative = x < 0n !== y < 0n;
  const scaled = (x < 0n ? -x : x) * 10n ** BigInt(dp + 1);
  const quotient = scaled / (y < 0n ? -y : y);
  const rounded = quotient / 10n + (quotient % 10n >= 5n ? 1n : 0n);

  return unscale({ units: negative ? -rounded : rounded, scale: dp });
}

export function cmpDecimal(a: string, b: string): -1 | 0 | 1 {
  const [x, y] = common(scale(a), scale(b));
  return x < y ? -1 : x > y ? 1 : 0;
}

export function isZeroDecimal(value: string): boolean {
  return scale(value).units === 0n;
}

export function isNegativeDecimal(value: string): boolean {
  return scale(value).units < 0n;
}

export function absDecimal(value: string): string {
  const { units, scale: dp } = scale(value);
  return unscale({ units: units < 0n ? -units : units, scale: dp });
}

export function negateDecimal(value: string): string {
  const { units, scale: dp } = scale(value);
  return unscale({ units: -units, scale: dp });
}
